import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  FormControlLabel,
  Radio,
  SvgIcon,
  Typography
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import { ChevronDown } from "lucide-react";
import { AvailabilityInfo } from "./Availability.component";
import { EditionHeader } from "./EditionHeader.component";
import { getModalEditionChangeStyles } from "./ModalEditionChange.style";
import { SessionItem } from "./SessionItem.component";
import { isDisabled, displayRadio, canSelfEnroll } from "../../components/TabsContent/Editions/utils";
import { capitalizeString, formatDateWithCapitalizedMonth } from "../../queries";
import { Edition, IEdition, LearningActivity, SessionWithTeachersAndTutors } from "../../types.d";

interface EditionCardProps {
  edition: IEdition;
  isVirtual: boolean;
  editMode: boolean;
  isExpanded: boolean;
  onToggleAccordion: () => void;
  theme: Theme;
  t: TFunction;
  learningObject: LearningActivity,
  oneEditionIsEnrolled: boolean,
}

export function EditionCard({
  edition,
  editMode,
  isVirtual,
  isExpanded,
  onToggleAccordion,
  theme,
  learningObject,
  t,
  oneEditionIsEnrolled
}: EditionCardProps) {

  const sessions = edition.sessions || [];

  const editionTitle = edition.editionName ||`${t("edition_name")} ${edition.id}`;
  const hasAvailablePlaces = edition.remainingPlaces > 0;

  const { selfEnrollment } = learningObject;
  const selfEnrollAllowed = canSelfEnroll(selfEnrollment, learningObject.enrollmentEnrollType);
  const isEnrolled = edition?.status === "E" || edition?.status === "P";
  const isCompleted = edition?.status === "C";

  const canEnroll = (edition: Edition): boolean => {
    if(oneEditionIsEnrolled && !editMode) return false;

    return edition.remainingPlaces > 0;
  };

  const editionStartTime = dayjs(edition.startTime);
  const editionEndTime = dayjs(edition.endTime);
  const editionDisabled = isDisabled(!canEnroll(edition), isEnrolled, isCompleted, hasAvailablePlaces, editionEndTime);

  const styles = getModalEditionChangeStyles(theme, isEnrolled, editionDisabled );
  const radioHidden = displayRadio(selfEnrollAllowed, oneEditionIsEnrolled, editMode) === "none";

  const getEditionDateRange = (
    t: TFunction
  ): string => {
    if (!editionStartTime || !editionEndTime) return "-";

    if (editionStartTime.format("DD/MM/YYYY") === editionEndTime.format("DD/MM/YYYY")) return formatDateWithCapitalizedMonth(editionStartTime);

    return `${capitalizeString(t("from"))} ${formatDateWithCapitalizedMonth(editionStartTime)} ${t("to")} ${formatDateWithCapitalizedMonth(editionEndTime)}`;
  };

  const dateRange = getEditionDateRange(t);

  return (
    <Box
      id={ `edition-card-${edition.id}` }
      data-testid="edition-card-root"
      role="group"
      aria-label={ editionTitle }
      sx={ { ...styles.editionCard.cardBox, opacity: editionDisabled ? 0.5 : 1 } }
    >
      <Accordion
        expanded={ isExpanded }
        disableGutters
        onChange={ onToggleAccordion }
        sx={ styles.editionCard.cardAccordion }
      >
        <AccordionSummary
          data-testid="edition-card-accordion-summary"
          expandIcon={ <ChevronDown size={ "1.25rem" } color={ theme.customColors.textPrimary } /> }
          aria-controls={ `edition-${edition.id}-content` }
          aria-expanded={ isExpanded }
          aria-label={ editionTitle }
          id={ `edition-${edition.id}-header` }
          sx={ {
            pl: 0,
            ".MuiAccordionSummary-content": {
              margin: "0 !Important"
            } }
          }
        >
          <Box sx={ {
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            justifyContent: "space-between",
            pl: 0,
            pr: 0,
            width: "100%",
            [theme.breakpoints.up("sm")]: {
              pl: radioHidden ? "12px" : 0,
              pr: radioHidden ? "12px" : 2
            }
          } }>
            <Box sx={ { alignItems: "center", display: "flex", flex: "1 1 auto", minWidth: 0 } }>
              <FormControlLabel
                data-testid="edition-card-radio-label"
                aria-label={ editionTitle }
                value={ edition.id }
                disabled={ editionDisabled }
                onClick={ (e) => e.stopPropagation() }
                control={
                  <Radio
                    id={ `edition-card-${edition.id}-radio` }
                    data-testid="edition-card-radio"
                    icon={
                      <SvgIcon viewBox="0 0 24 24">
                        { /* cerchio interno default */ }
                        <circle cx="12" cy="12" r="10" stroke={ theme.customColors.border } strokeWidth="1" fill={ theme.customColors.backgroundDisabled } />
                      </SvgIcon>
                    }
                    checkedIcon={
                      <SvgIcon viewBox="0 0 24 24">
                        { /* cerchio esterno bianco con cerchio interno color border */ }
                        <circle cx="12" cy="12" r="10" stroke={ theme.customColors.border } strokeWidth="1" fill="transparent" />
                        <circle cx="12" cy="12" r="5" fill={ theme.customColors.backgroundPrimaryCta } />
                      </SvgIcon>
                    }
                    sx={ {
                      ...styles.editionCard.cardRadio,
                      display: displayRadio(selfEnrollAllowed, oneEditionIsEnrolled, editMode) // hide button if user cant self enroll
                    } }
                  />
                }
                label=""
                sx={ { m: 0, mx: 1 } }
              />
              <Box sx={ { minWidth: 0 } }>
                <EditionHeader
                  editionId={ edition.id }
                  code={ edition.editionCode }
                  location={ edition.location }
                  isVirtual={ isVirtual }
                  theme={ theme }
                  t={ t }
                />
                <Typography fontWeight={ 700 } sx={ { color: theme.customColors.textPrimary } }>
                  { editionTitle }
                </Typography>
                <Typography  sx={ { color: theme.customColors.textPrimary, fontSize: "0.75rem", mt: 0.5 } }>
                  { dateRange }
                </Typography>
              </Box>
            </Box>

            <Box
              sx={ {
                alignItems: "flex-start",
                display: "flex",
                flexDirection: "column",
                flexShrink: 0,
                ml: 0,
                pl: "12px",
                [theme.breakpoints.up("sm")]: {
                  alignItems: "flex-end",
                  ml: "auto",
                  pl: 0
                }
              } }
            >
              <AvailabilityInfo
                hasAvailablePlaces={ hasAvailablePlaces }
                remainingPlaces={ edition.remainingPlaces }
                theme={ theme }
                t={ t }
                isEnrolled={ isEnrolled }
                oneEditionIsEnrolled={ oneEditionIsEnrolled }
                editMode={ editMode }
                selfEnrollment={ selfEnrollAllowed }
              />
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={ {
            padding: "1rem 1rem 0rem"
          } }
        >
          { edition.extraInfo?.trim() && (
            <Box data-testid="edition-card-extra-info">
              <Divider />
              <Typography
                sx={ {
                  color: theme.customColors.textPrimary,
                  fontSize: "0.875rem",
                  overflowWrap: "anywhere",
                  padding: "12px 0",
                  whiteSpace: "pre-wrap",
                  [theme.breakpoints.up("sm")]: {
                    padding: "12px"
                  }
                } }
              >
                <Box component="span" sx={ { fontWeight: 700 } }>
                  { t("info") }:
                </Box>
                { " " }
                { edition.extraInfo }
              </Typography>
            </Box>
          ) }
          { sessions
            .sort((a, b) => Number(a.startDate) - Number(b.startDate))
            .map((session: SessionWithTeachersAndTutors, index: number) => (
              <Box key={ index }>
                { index !== sessions.length && <Divider /> }
                <SessionItem
                  index={ index }
                  totalSessions={ sessions.length }
                  session={ session }
                  isVirtual={ isVirtual }
                  theme={ theme }
                  t={ t }
                  showStaff={ isEnrolled || isCompleted }
                />
              </Box>
            )) }
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
